import type { DistanceBearingPitch } from '@ikegps/core-api';
import * as IkeModel from '@ikegps/core-shared';
import { CollectionModelFieldVector, MultiPoint } from '@ikegps/core-shared';
import { isEqual } from 'lodash';
import { AnchorInput } from '../../../components/input/anchor.input';
import { AnchorInputState } from '../../../components/input/anchor.input.state';
import { T, Translate } from '../../../i18n';
import type { MithrilVNode } from '../../../mithril';
import { FieldInfo, type FieldStat } from '../../field.info/field.info';
import { TopologicalPointCollectionCache } from '../../topological.editor.map/point.collection.cache';
import type { ICollectionViewController } from '../collection.view.ctrl.interface';
import type { CollectionFieldView } from './field.view';

export function viewAnchorManualEdit(
	field: IkeModel.CollectionModelFieldAnchor,
	ctrl: ICollectionViewController
): MithrilVNode {
	let inputState = ctrl.changeState.getState(field.id) as AnchorInputState;

	// convert GPS to vector
	const source = TopologicalPointCollectionCache.getCollectionLocation(field.collection.revision);
	const distanceBearingPitch: DistanceBearingPitch = field.distanceBearingPitch(source);

	if (!inputState) {
		const { value } = field;

		inputState = new AnchorInputState(field);
		inputState.onValue(() => {
			field.Mutator.manual(inputState.value);

			// This will ensure topo map is updated (if it exists) and a user is updating vector locations
			TopologicalPointCollectionCache.dropCollectionVectors(field.collection.revision);
		});

		ctrl.changeState.addState(field.id, inputState);

		if (value != null && distanceBearingPitch != null) {
			inputState.setValue({
				distance: distanceBearingPitch.distance,
				bearing: distanceBearingPitch.bearing,
				pitch: distanceBearingPitch.pitch,
				placement: value.placement,
				relatedSpanIds: value.relatedSpanIds,
				collectionTool: 'VECTOR',
			});
		}
	}

	const linkableSpanFields = [];

	field.collection.forEachField((x) => {
		if (CollectionModelFieldVector.isVectorField(x)) {
			linkableSpanFields.push(x);
		}
	});

	if (
		!isEqual(
			linkableSpanFields.sort((a, b) => a - b),
			inputState.cachedSpanFields
		)
	) {
		inputState.populateRelatedSpansOptions(linkableSpanFields);
	}

	// If none of the inputs are focused, and the field model value changes
	// the input state should also update.
	if (
		field.value !== null &&
		distanceBearingPitch !== null &&
		!(
			inputState.distance.meters.focused ||
			inputState.distance.inch.focused ||
			inputState.distance.feet.focused ||
			inputState.bearing.focused
		)
	) {
		inputState.setValue({
			...distanceBearingPitch,
			placement: inputState.placement.value,
			relatedSpanIds: inputState.relatedSpans.map((x) => x.value),
			collectionTool: 'VECTOR',
		});
	}

	return AnchorInput.view(inputState, ctrl, field);
}

export const AnchorManualFieldView: CollectionFieldView<IkeModel.CollectionModelFieldAnchor> = {
	hasClear() {
		return false;
	},

	viewInline(field: IkeModel.CollectionModelFieldAnchor): string | null {
		if (!field.hasValue()) {
			return null;
		}
		// convert location to distance/bearing
		const source = TopologicalPointCollectionCache.getCollectionLocation(field.collection.revision);
		if (!source) {
			return null;
		}
		const distanceBearingPitch: DistanceBearingPitch = field.distanceBearingPitch(source);

		return `${FieldInfo.viewVectorInline(distanceBearingPitch)}, Alignment: ${Translate(field.value.placement)}`;
	},

	hasDetail() {
		return false;
	},

	viewDetail(): MithrilVNode {
		return null;
	},

	viewEdit(field: IkeModel.CollectionModelFieldAnchor, ctrl: ICollectionViewController): MithrilVNode | string {
		return viewAnchorManualEdit(field, ctrl);
	},
};

export const AnchorP2PFieldView: CollectionFieldView<IkeModel.CollectionModelFieldAnchor> = {
	viewInline(field: IkeModel.CollectionModelFieldAnchor): string | null {
		if (!field.hasValue()) {
			return null;
		}
		// convert location to distance/bearing
		const source = TopologicalPointCollectionCache.getCollectionLocation(field.collection.revision);
		if (!source) {
			return null;
		}
		const distanceBearingPitch = field.distanceBearingPitch(source);
		if (distanceBearingPitch == null) {
			return null;
		}
		return `${FieldInfo.viewVectorInline(distanceBearingPitch)}, Alignment: ${Translate(field.value.placement)}`;
	},

	hasDetail() {
		return true;
	},

	viewDetail(field: IkeModel.CollectionModelFieldAnchor): MithrilVNode {
		const collection = field.collection;
		const captures = field.getCaptures();
		const p2pMeasurement = MultiPoint.Points(captures);

		const stats: FieldStat[] = [
			{ name: T.DISTANCE_VERTICAL, value: Math.abs(p2pMeasurement.vertical) },
			{ name: T.DISTANCE_HORIZONTAL, value: p2pMeasurement.horizontal },
		];

		return FieldInfo.viewCaptures(collection, captures, stats);
	},

	viewEdit(field: IkeModel.CollectionModelFieldAnchor, ctrl: ICollectionViewController): MithrilVNode {
		return viewAnchorManualEdit(field, ctrl);
	},
};
